import { MockDatabase, MockRecord, MockTable } from "../MockDatabase";

export type AttributionListAPI = {
    attribution_guid: string;
    display_name: string;
    status: string;
    type: string;
    created_date: string;
    updated_date: string;
}

export type AttributionAPI = {
    attribution_guid: string;
    display_name: string;
    status: string;
    type: string;
    created_date: string;
    updated_date: string;
    type_version: number;
    properties_version: number;
    properties: Record<string, any>;
}

export class MockAttributions extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'attributions';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            attribution_guid: this.PublicKey({
                fieldName: 'attribution_guid'
            }),
            // Since this needs to point to the latest record of schema data with a matching schema_data_id,
            // we can't set it as a foreign key as the "related" array will be out of date
            // Don't use getRelated on this property
            schema_data_id: this.Field({
                type: 'string',
                fieldName: 'schema_data_id',
                nullable: true
            }),
            status: this.Field({
                type: 'string',
                fieldName: 'status',
                nullable: false
            }),
            display_name: this.Field({
                type: 'string',
                fieldName: 'display_name',
                nullable: false
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
            type: this.Field({
                type: 'string',
                fieldName: 'type',
                nullable: false
            })
        } as const;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        const {type_version, ...rem} = data;
        return rem;
    }

    private _baseSerialize = (record: MockRecord) => ({
        attribution_guid: record.values.attribution_guid,
        display_name: record.values.display_name,
        status: record.values.status,
        type: record.values.type,
        created_date: record.values.created_date,
        updated_date: record.values.updated_date,
    }) as AttributionAPI;

    serialize = (record: MockRecord, db: MockDatabase) => {
        const schema_data_id = record.values['schema_data_id'];
        let properties: Record<string, any> = {};
        let type_version: number | undefined;
        if (schema_data_id !== null) {
            const schemaDataTable = db.getTableByName('schema_data');
            const schemaDataRec = schemaDataTable.one([
                {
                    fieldName: 'schema_data_id',
                    value: record.values['schema_data_id']
                },
                {
                    fieldName: 'latest',
                    value: 1
                }
            ]);
            if (schemaDataRec) {
                type_version = schemaDataRec.values['schema_data_version'];
                try {
                    properties = JSON.parse(schemaDataRec.values['data']);
                } catch(e) {
                    console.warn(`Failed to parse schema data for attribution "${record.values['id']}" during serialization`);
                }
            }
        }
        return {
            ...this._baseSerialize(record),
            type_version,
            properties
        }
    }

    serializeList = (records: MockRecord[], db: MockDatabase) => records.map(record => (
        this._baseSerialize(record)
    )) as AttributionListAPI[];
}